import { CommonModule } from '@angular/common';
import { Component, OnDestroy, SecurityContext, ViewChild } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { DomSanitizer } from '@angular/platform-browser';
import { ImportIntegration } from '@app/models/import/import-integration.model';
import { ImportIntegrationService } from '@app/modules/import/services';
import { IntegrationStep } from '@app/modules/import/services/integration/import-integration.service';
import { PlatformModule } from '@app/platform.module';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { INTEGRATIONS_HELP_LINK } from '../../integrations-import.view';

@Component({
    selector: 'app-integrations-connection-preview',
    templateUrl: './integrations-connection-preview.template.html',
    standalone: true,
    imports: [CommonModule, TranslateModule, PlatformModule],
    styleUrls: ['./integrations-connection-preview.style.scss', '../integration-steps.scss'],
})
export class IntegrationsConnectionPreviewStep implements OnDestroy {
    // ! For now these are being hard-coded. In future iterations of the BE API we will likely retrieve these from Unified
    readonly VISIBLE_FIELDS = [
        'names.first',
        'names.last',
        'emails',
        'title',
        'department',
        'hired_at',
        'role_permissions',
        'employment_type',
        'office_name',
    ];

    integrationsHelpLink = INTEGRATIONS_HELP_LINK;

    @ViewChild(MatExpansionPanel) private instructionsPanel?: MatExpansionPanel;

    private _integration: ImportIntegration | null = null;
    private _connectionURL?: string;
    private _sanitizedConnectionInstructions?: (string | null)[];
    private _destroyed$: Subject<void> = new Subject<void>();
    private _isErrored = false;

    get connectionURL(): string | undefined {
        return this._connectionURL;
    }

    get isErrored(): boolean {
        return this._isErrored;
    }

    get integration(): ImportIntegration | null {
        return this._integration;
    }

    get sanitizedConnectionInstructions(): (string | null)[] | undefined {
        return this._sanitizedConnectionInstructions;
    }

    constructor(
        public integrationService: ImportIntegrationService,
        private domSanitizer: DomSanitizer
    ) {
        this.integrationService.integrationInProgress.pipe(takeUntil(this._destroyed$)).subscribe((integration) => {
            this._integration = integration;
            if (integration) {
                this.getConnectionURL(integration);
                this.setSanitizedConnectionInstructions(integration);
            } else {
                // The user has navigated to the LIST step or some other action that has removed an integration partner from context
                // Therefore we reset the state of this component
                this._isErrored = false;
                this._connectionURL = undefined;
                this.instructionsPanel?.close();
            }
        });
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    onBack(): void {
        this.integrationService.goToStep(IntegrationStep.LIST);
    }

    private setSanitizedConnectionInstructions(integration: ImportIntegration): void {
        this._sanitizedConnectionInstructions = integration.tokenInstructions?.map((instruction) => {
            const linksInNewTab = instruction.replace(/<a (.*)>/g, '<a target="_blank" $1>'); // Unified sends links in the same tab. We don't want to navigate users away from our app, therefore we add a target="_blank" to all anchor tags
            return this.domSanitizer.sanitize(SecurityContext.HTML, linksInNewTab); // Sanitize to ensure no script tags are included and dangerously rendered within our app
        });
    }

    private getConnectionURL(integration: ImportIntegration): void {
        this.integrationService
            .getIntegrationConnection(integration)
            .then((connection) => {
                const url = connection?.url;
                if (!url || !url.startsWith('http')) {
                    throw new Error('Invalid URL');
                }
                this._connectionURL = url;
            })
            .catch(() => {
                this._isErrored = true;
            });
    }
}
